import { generateUUID } from "@/lib/uuid";
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";
import { useFiinderApi } from "@/components/api/fiinder-api-context";
import { useTenantUserId } from "@/components/api/tenant-user-id-provider";
import type {
  ConnectionStatus,
  FeedbackScore,
  Message,
} from "@/components/api/fiinder-api-types";
import React from "react";

const shouldLogMessages =
  typeof window !== "undefined" &&
  (window as { __FIINDER_DEBUG_MESSAGES__?: boolean })
    .__FIINDER_DEBUG_MESSAGES__ === true;

// function generateMessageId() {
//   return Math.random().toString(36).slice(2, 10)
// }

function getMessageQueryKey(id: string): ["message", string] {
  return ["message", id];
}

function isValidIsoDate(value: string | undefined): value is string {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp);
}

function toTimestamp(value: string | undefined): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function isValidMessageId(id: string | undefined | null): id is string {
  return typeof id === "string" && id.length > 0;
}

function removeOptimisticDuplicate(props: {
  ids: string[];
  excluded: Set<string>;
  queryClient: QueryClient;
  message: Message;
}): string | undefined {
  const { ids, excluded, queryClient, message } = props;
  for (const id of ids) {
    if (excluded.has(id)) continue;
    if (!id.startsWith("temp-")) continue;
    const optimistic = queryClient.getQueryData<Message>(getMessageQueryKey(id));
    if (!optimistic) continue;
    if (
      optimistic.content === message.content &&
      optimistic.role === message.role
    ) {
      return id;
    }
  }
  return undefined;
}

// Core function that processes messages and returns ordered keys
// Used by both processAndCacheMessages and updateCacheWithNewMessages
function processMessagesCore(props: {
  queryClient: QueryClient;
  messages: Message[];
  roomId: string;
  prevKeys: ["message", string][];
}): ["message", string][] {
  const { queryClient, messages, roomId, prevKeys } = props;

  const prevIds = prevKeys.map(([, id]) => id);
  const previousOrder = new Map(prevIds.map((id, index) => [id, index]));

  const idsToDrop = new Set<string>();
  const normalizedMessages: Message[] = [];

  for (const incoming of messages) {
    // Skip messages with invalid ids
    if (!isValidMessageId(incoming.id)) {
      continue;
    }
    const targetRoomId = incoming.roomId ?? roomId;
    if (targetRoomId !== roomId) continue;

    const existing = queryClient.getQueryData<Message>(
      getMessageQueryKey(incoming.id)
    );

    const timestampCandidate = isValidIsoDate(incoming.timestamp)
      ? incoming.timestamp
      : existing?.timestamp;

    const normalized: Message = {
      ...existing,
      ...incoming,
      roomId: targetRoomId,
      timestamp: isValidIsoDate(timestampCandidate)
        ? timestampCandidate
        : new Date().toISOString(),
    };

    queryClient.setQueryData(getMessageQueryKey(normalized.id), normalized);
    normalizedMessages.push(normalized);

    const optimisticId = removeOptimisticDuplicate({
      ids: prevIds,
      excluded: idsToDrop,
      queryClient,
      message: normalized,
    });

    if (optimisticId) {
      idsToDrop.add(optimisticId);
      queryClient.removeQueries({
        queryKey: getMessageQueryKey(optimisticId),
        exact: true,
      });
    }
  }

  if (normalizedMessages.length === 0) return prevKeys;

  const combinedIds: string[] = [];
  for (const id of prevIds) {
    if (!idsToDrop.has(id)) {
      combinedIds.push(id);
    }
  }
  for (const msg of normalizedMessages) {
    combinedIds.push(msg.id);
  }

  const uniqueIds: string[] = [];
  const seenIds = new Set<string>();
  for (const id of combinedIds) {
    // Skip invalid ids
    if (!isValidMessageId(id)) continue;
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    uniqueIds.push(id);
  }

  const messagesForSort: Message[] = uniqueIds
    .map((id) => queryClient.getQueryData<Message>(getMessageQueryKey(id)))
    .filter((msg): msg is Message => Boolean(msg));

  messagesForSort.sort((a, b) => {
    const tsA = toTimestamp(a.timestamp);
    const tsB = toTimestamp(b.timestamp);
    const diff = tsA - tsB;

    if (diff !== 0) return diff;

    if (a.role !== b.role) {
      return a.role === "user" ? -1 : 1;
    }

    return a.id.localeCompare(b.id);
  });

  return messagesForSort.map((msg) => ["message", msg.id] as ["message", string]);
}

// Process messages and return ordered keys directly (used for initial fetch)
function processAndCacheMessages(props: {
  queryClient: QueryClient;
  messages: Message[];
  roomId: string | undefined;
}): ["message", string][] {
  const { queryClient, messages, roomId } = props;
  if (!roomId || messages.length === 0) return [];

  const prevKeys =
    queryClient.getQueryData<["message", string][]>(["messages", roomId]) || [];

  const orderedKeys = processMessagesCore({
    queryClient,
    messages,
    roomId,
    prevKeys,
  });

  queryClient.setQueryData(["messages", roomId], orderedKeys);
  return orderedKeys;
}

// Update cache with new messages (used for websocket updates)
function updateCacheWithNewMessages(props: {
  queryClient: QueryClient;
  messages: Message[];
  roomId: string | undefined;
}) {
  const { queryClient, messages, roomId } = props;
  if (!roomId || messages.length === 0) return;

  const prevKeys =
    queryClient.getQueryData<["message", string][]>(["messages", roomId]) || [];

  const orderedKeys = processMessagesCore({
    queryClient,
    messages,
    roomId,
    prevKeys,
  });

  queryClient.setQueryData(["messages", roomId], orderedKeys);
}

export function useRequestNewRoomId() {
  const api = useFiinderApi();
  return (args?: {}) => api.requestNewRoomId(args);
}

export function useMessages(roomId: string | undefined) {
  const queryClient = useQueryClient();
  const api = useFiinderApi();
  const tenantUserId = useTenantUserId();

  React.useEffect(() => {
    function handleAgentMessage(message: Message) {
      // Skip messages with invalid ids
      if (!isValidMessageId(message.id)) {
        return;
      }
      updateCacheWithNewMessages({
        queryClient,
        messages: [message],
        roomId: message.roomId ?? roomId,
      });
      if (shouldLogMessages) {
        const ordered =
          queryClient.getQueryData<["message", string][]>([
            "messages",
            message.roomId ?? roomId,
          ]) || [];
        console.debug("[chat] message received", {
          id: message.id,
          role: message.role,
          timestamp: message.timestamp,
          orderedIds: ordered.map(([, id]) => id),
        });
      }
    }
    api.messageEmitter.on("message", handleAgentMessage);
    return () => {
      api.messageEmitter.off("message", handleAgentMessage);
    };
  }, [api, roomId, queryClient]);

  // Fetch messages when roomId changes (including restoration from persisted state)
  const fetchQuery = useQuery<["message", string][]>({
    queryKey: ["messages", roomId],
    queryFn: async () => {
      if (!roomId) return [];
      const messages = await api.fetchMessages({ roomId, tenantUserId });

      // Process messages and get ordered keys directly
      // instead of relying on getQueryData which can have race conditions
      const orderedKeys = processAndCacheMessages({
        queryClient,
        messages,
        roomId,
      });

      return orderedKeys;
    },
    enabled: !!roomId,
    staleTime: 0,
    refetchOnMount: "always",
    // Show cached data while fetching fresh data
    placeholderData: (previousData) => previousData,
  });

  return fetchQuery;
}

// Helper to get messages from IDs
export function useMessagesFromIds(ids: ["message", string][]) {
  // `useQueries` subscribes to each per-message cache key so the consumer
  // re-renders whenever any single message updates. The previous
  // `getQueryData` snapshot only sampled at render time and missed all
  // streaming deltas, which is why agent responses appeared in one shot
  // instead of word by word.
  const results = useQueries({
    queries: ids.map((key) => ({
      queryKey: key,
      // No fetcher — the cache is populated by the websocket message
      // emitter and the optimistic mutation. We just want a subscription.
      queryFn: () => Promise.resolve(undefined as unknown as Message),
      enabled: false,
      staleTime: Infinity,
      gcTime: Infinity,
    })),
  });
  return results
    .map((r) => r.data as Message | undefined)
    .filter(Boolean) as Message[];
}

export function useSendMessage(roomId: string | undefined) {
  const api = useFiinderApi();
  const queryClient = useQueryClient();
  const tenantUserId = useTenantUserId();

  return useMutation({
    mutationFn: async (message: Pick<Message, "content">) => {
      const messageId = await api.sendMessage({ roomId, message, tenantUserId });
      return {
        ...message,
        id: messageId,
        role: "user" as const,
        timestamp: new Date().toISOString(),
        roomId,
      };
    },
    onMutate: async (message: Pick<Message, "content">) => {
      await queryClient.cancelQueries({ queryKey: ["messages", roomId] });
      const previousIds =
        queryClient.getQueryData<["message", string][]>(["messages", roomId]) ||
        [];
      const tempId = `temp-${Math.random().toString(36).slice(2, 10)}`;

      const tempMsg: Message = {
        ...message,
        role: "user",
        id: tempId,
        timestamp: new Date().toISOString(),
        roomId,
      };

      updateCacheWithNewMessages({
        queryClient,
        messages: [tempMsg],
        roomId: tempMsg.roomId ?? roomId,
      });

      if (shouldLogMessages) {
        const ordered =
          queryClient.getQueryData<["message", string][]>([
            "messages",
            tempMsg.roomId ?? roomId,
          ]) || [];
        console.debug("[chat] user message queued", {
          id: tempMsg.id,
          role: tempMsg.role,
          timestamp: tempMsg.timestamp,
          orderedIds: ordered.map(([, id]) => id),
        });
      }
      return { previousIds, tempId };
    },
    onError: (error, _, context) => {
      if (context?.previousIds) {
        queryClient.setQueryData(["messages", roomId], context.previousIds);
      }
    },
    onSuccess: (msg, _, context) => {
      // msg.id should now contain the real message ID from the server
      // Fall back to tempId if server returns empty/null, or generate UUID as last resort
      const serverIdValid = isValidMessageId(msg.id);
      const messageId = serverIdValid
        ? msg.id
        : (context?.tempId || generateUUID());

      const realMsg: Message = {
        ...msg,
        id: messageId,
        roomId,
      };
      // Use updateCacheWithNewMessages to add the real message and remove the temp one
      // Remove the temp message from the cache
      if (context?.tempId) {
        queryClient.removeQueries({ queryKey: ["message", context.tempId] });
        queryClient.setQueryData(
          ["messages", roomId],
          (old: ["message", string][]) => {
            return old.filter(([_, id]) => id !== context.tempId);
          }
        );
      }
      updateCacheWithNewMessages({
        queryClient,
        messages: [realMsg],
        roomId: realMsg.roomId ?? roomId,
      });
    },
    // Note: We intentionally do NOT invalidate queries here.
    // The websocket already updates the cache in real-time via messageEmitter.
    // Calling invalidateQueries would trigger a refetch that returns stale data
    // from the server (missing streamed messages not yet persisted to DB).
  });
}

export function useSubmitFeedback() {
  const api = useFiinderApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, score }: { messageId: string; score: FeedbackScore }) => {
      await api.submitFeedback({ messageId, score });
      return { messageId, score };
    },
    onMutate: async ({ messageId, score }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["message", messageId] });

      // Snapshot the previous value
      const previousMessage = queryClient.getQueryData<Message>(["message", messageId]);

      // Optimistically update to the new value
      if (previousMessage) {
        queryClient.setQueryData<Message>(["message", messageId], {
          ...previousMessage,
          feedbackScore: score,
        });
      }

      return { previousMessage };
    },
    onError: (error, { messageId }, context) => {
      // Rollback to previous value
      if (context?.previousMessage) {
        queryClient.setQueryData(["message", messageId], context.previousMessage);
      }
      console.error("[FeedbackSubmit] Failed to submit feedback", { messageId, error });
    },
    onSuccess: ({ messageId, score }) => {
      // Cache is already updated optimistically, but ensure it's correct
      const message = queryClient.getQueryData<Message>(["message", messageId]);
      if (message && message.feedbackScore !== score) {
        queryClient.setQueryData<Message>(["message", messageId], {
          ...message,
          feedbackScore: score,
        });
      }
    },
  });
}

export function useConnectionStatus(): ConnectionStatus {
  const api = useFiinderApi();
  const [status, setStatus] = React.useState<ConnectionStatus>(() =>
    api.getConnectionStatus()
  );

  React.useEffect(() => {
    const unsubscribe = api.subscribeToConnectionStatus((nextStatus) => {
      setStatus(nextStatus);
    });
    return unsubscribe;
  }, [api]);

  return status;
}
